import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { useDesign } from './DesignContext';

const TypographyContext = createContext();

// Base default settings with new categories (using points 'pt')
const baseDefaultSettings = {
  cvTitle: { // For First Name / Last Name
    fontSize: 24, // Example size
    fontFamily: "'Helvetica', sans-serif",
    lineHeight: 1.2,
  },
  sectionTitle: { // For section headings like "Work Experience"
    fontSize: 14, // Example size
    fontFamily: "'Helvetica', sans-serif",
    lineHeight: 1.3,
  },
  body: { // For main content text, roles, descriptions
    fontSize: 10, // Example size
    fontFamily: "'Arial', sans-serif",
    lineHeight: 1.4,
  },
  smallText: { // For dates, locations, sub-bullets
    fontSize: 8.5, // Example size
    fontFamily: "'Arial', sans-serif",
    lineHeight: 1.3,
  },
  // Removed old categories: heading, subheading
};

export function TypographyProvider({ children }) {
  const { activeDesign, selectedDesign } = useDesign();
  const isInitialized = useRef(false);

  const [typographySettings, setTypographySettings] = useState(() => {
    const designDefaults = activeDesign?.typography || {}; // Assume design defaults might use new structure

    try {
      const savedSettings = localStorage.getItem('typographySettings');
      if (savedSettings) {
        const parsedSettings = JSON.parse(savedSettings);

        // Merge with base and design defaults using the NEW structure
        return {
          ...baseDefaultSettings,
          ...designDefaults, // Apply design defaults first (might override base)
          ...parsedSettings, // Apply saved settings last (user overrides)
          // Deep merge each section to ensure all properties exist
          cvTitle: {
            ...baseDefaultSettings.cvTitle,
            ...(designDefaults.cvTitle || {}),
            ...(parsedSettings.cvTitle || {}),
          },
          sectionTitle: {
            ...baseDefaultSettings.sectionTitle,
            ...(designDefaults.sectionTitle || {}),
            ...(parsedSettings.sectionTitle || {}),
          },
          body: {
            ...baseDefaultSettings.body,
            ...(designDefaults.body || {}),
            ...(parsedSettings.body || {}),
          },
          smallText: {
            ...baseDefaultSettings.smallText,
            ...(designDefaults.smallText || {}),
            ...(parsedSettings.smallText || {}),
          },
        };
      }

      // No saved settings, use design defaults merged with base (NEW structure)
      return {
        ...baseDefaultSettings,
        ...designDefaults,
        cvTitle: {
          ...baseDefaultSettings.cvTitle,
          ...(designDefaults.cvTitle || {}),
        },
        sectionTitle: {
          ...baseDefaultSettings.sectionTitle,
          ...(designDefaults.sectionTitle || {}),
        },
        body: {
          ...baseDefaultSettings.body,
          ...(designDefaults.body || {}),
        },
        smallText: {
          ...baseDefaultSettings.smallText,
          ...(designDefaults.smallText || {}),
        },
      };
    } catch (error) {
      console.error('Failed to load typography settings:', error);
      // Fallback uses base and design defaults (NEW structure)
      return {
        ...baseDefaultSettings,
        ...designDefaults,
         cvTitle: { ...baseDefaultSettings.cvTitle, ...(designDefaults.cvTitle || {}) },
         sectionTitle: { ...baseDefaultSettings.sectionTitle, ...(designDefaults.sectionTitle || {}) },
         body: { ...baseDefaultSettings.body, ...(designDefaults.body || {}) },
         smallText: { ...baseDefaultSettings.smallText, ...(designDefaults.smallText || {}) },
      };
    }
  });

  // Update settings when design changes (using NEW structure)
  useEffect(() => {
    const designDefaults = activeDesign?.typography || {};

    setTypographySettings(current => ({
      ...baseDefaultSettings,
      ...designDefaults,
      ...current, // Keep user's current overrides if they exist
      // Deep merge specific sections
      cvTitle: {
        ...baseDefaultSettings.cvTitle,
        ...(designDefaults.cvTitle || {}),
        ...current.cvTitle,
      },
      sectionTitle: {
         ...baseDefaultSettings.sectionTitle,
         ...(designDefaults.sectionTitle || {}),
         ...current.sectionTitle,
      },
      body: {
        ...baseDefaultSettings.body,
        ...(designDefaults.body || {}),
        ...current.body,
      },
      smallText: {
        ...baseDefaultSettings.smallText,
        ...(designDefaults.smallText || {}),
        ...current.smallText,
      },
    }));
  }, [selectedDesign, activeDesign]);

  // Function to reset typography to design defaults (using NEW structure)
  const resetToDesignDefaults = () => {
    const designDefaults = activeDesign?.typography || {};

    setTypographySettings({
      ...baseDefaultSettings,
      ...designDefaults,
      cvTitle: {
        ...baseDefaultSettings.cvTitle,
        ...(designDefaults.cvTitle || {}),
      },
      sectionTitle: {
         ...baseDefaultSettings.sectionTitle,
         ...(designDefaults.sectionTitle || {}),
      },
      body: {
        ...baseDefaultSettings.body,
        ...(designDefaults.body || {}),
      },
      smallText: {
        ...baseDefaultSettings.smallText,
        ...(designDefaults.smallText || {}),
      },
    });
  };

  // Update function using NEW structure
  const wrappedUpdateTypographySettings = (newSettings) => {
    console.log('TypographyContext: Updating settings', newSettings);
    try {
      const updatedSettings = {
        ...typographySettings,
        ...newSettings,
        // Deep merge specific sections
        cvTitle: {
          ...typographySettings.cvTitle,
          ...(newSettings.cvTitle || {}),
        },
        sectionTitle: {
          ...typographySettings.sectionTitle,
          ...(newSettings.sectionTitle || {}),
        },
        body: {
          ...typographySettings.body,
          ...(newSettings.body || {}),
        },
        smallText: {
          ...typographySettings.smallText,
          ...(newSettings.smallText || {}),
        },
      };

      isInitialized.current = true;
      setTypographySettings(updatedSettings);
    } catch (error) {
      console.error('Error updating typography settings:', error);
    }
  };

  // Save to localStorage (no changes needed here, structure is handled above)
  useEffect(() => {
    if (isInitialized.current) {
      try {
        localStorage.setItem('typographySettings', JSON.stringify(typographySettings));
        console.log('Saved typography settings to localStorage');
      } catch (error) {
        console.error('Error saving typography settings to localStorage:', error);
      }
    } else {
      // Mark as initialized after first render
      isInitialized.current = true;
    }
  }, [typographySettings]);

  // Handle imported settings (using NEW structure)
  useEffect(() => {
    // Check for imported typography settings on mount
    try {
      const importedSettings = localStorage.getItem('importedTypographySettings');
      if (importedSettings) {
        const parsedSettings = JSON.parse(importedSettings);
        // Ensure the imported settings conform to the new structure before updating
        const completeSettings = {
          ...baseDefaultSettings, // Start with base defaults
          ...parsedSettings,     // Overlay imported data
          // Deep merge sections from imported data
          cvTitle: { ...baseDefaultSettings.cvTitle, ...(parsedSettings.cvTitle || {}) },
          sectionTitle: { ...baseDefaultSettings.sectionTitle, ...(parsedSettings.sectionTitle || {}) },
          body: { ...baseDefaultSettings.body, ...(parsedSettings.body || {}) },
          smallText: { ...baseDefaultSettings.smallText, ...(parsedSettings.smallText || {}) },
        };
        wrappedUpdateTypographySettings(completeSettings); // Use the update function
        localStorage.removeItem('importedTypographySettings');
      }
    } catch (error) {
      console.error('Failed to apply imported typography settings:', error);
    }
  }, []); // Run only on mount

  // Listen for typography import events (using NEW structure)
  useEffect(() => {
    const handleImport = () => {
      try {
        console.log('Typography import event received');
        const importedSettings = localStorage.getItem('importedTypographySettings');
        if (importedSettings) {
          const parsedSettings = JSON.parse(importedSettings);
          console.log('Imported typography settings:', parsedSettings);

          // Create a complete settings object with the NEW structure
          const completeSettings = {
            ...baseDefaultSettings,
            ...parsedSettings,
            cvTitle: {
              ...baseDefaultSettings.cvTitle,
              ...(parsedSettings.cvTitle || {})
            },
            sectionTitle: {
              ...baseDefaultSettings.sectionTitle,
              ...(parsedSettings.sectionTitle || {})
            },
            body: {
              ...baseDefaultSettings.body,
              ...(parsedSettings.body || {})
            },
            smallText: {
              ...baseDefaultSettings.smallText,
              ...(parsedSettings.smallText || {})
            }
          };

          // Set the state directly first for immediate update
          setTypographySettings(completeSettings);

          localStorage.removeItem('importedTypographySettings');
          console.log('Typography settings updated from import');
        }
      } catch (error) {
        console.error('Failed to apply imported typography settings from event:', error);
      }
    };

    window.addEventListener('typographySettingsImported', handleImport);
    return () => {
      window.removeEventListener('typographySettingsImported', handleImport);
    };
  }, []); // Empty dependency array is correct here

  return (
    <TypographyContext.Provider value={{
      typographySettings,
      updateTypographySettings: wrappedUpdateTypographySettings,
      resetToDesignDefaults
    }}>
      {children}
    </TypographyContext.Provider>
  );
}

export function useTypography() {
  const context = useContext(TypographyContext);
  if (!context) {
    throw new Error('useTypography must be used within a TypographyProvider');
  }
  return context;
} 